import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface AgentItem {
  id: string; // Admin ID (UUID)
  phone: string; // 10 digit phone number
  // The app's own flag; null if the Admin account no longer exists
  subscription_status: 'active' | 'inactive' | string | null;
  plan_id: string;
  plan_name: string | null; // e.g. "Demo" or "Pro"; null if the plan id is unknown
  is_active: boolean; // Owner panel roster flag
  // Only GET one returns timestamps; the list does not
  created_at?: string | null;
  activated_at?: string | null;
  deactivated_at?: string | null;
  // Migration 0035: set while soft-deleted, null otherwise
  deleted_at?: string | null;

  // Compatibility / UI helper fields
  isActive?: boolean;
}

export interface AgentState {
  agents: AgentItem[];
  selectedAgent: AgentItem | null;
  filterStatus: 'all' | 'active' | 'inactive';
  loading: boolean;
  error: string | null;
}

export const normalizeAgent = (item: any): AgentItem => {
  const is_active =
    item.is_active !== undefined
      ? Boolean(item.is_active)
      : item.isActive !== undefined
      ? Boolean(item.isActive)
      : true;

  return {
    id: String(item.id || ''),
    phone: String(item.phone || '').trim(),
    subscription_status: item.subscription_status ?? null,
    plan_id: String(item.plan_id || ''),
    plan_name: item.plan_name ?? null,
    is_active,
    created_at: item.created_at ?? null,
    activated_at: item.activated_at !== undefined ? item.activated_at : null,
    deactivated_at: item.deactivated_at !== undefined ? item.deactivated_at : null,
    deleted_at: item.deleted_at ?? null,

    // UI helper
    isActive: is_active,
  };
};

const initialState: AgentState = {
  agents: [],
  selectedAgent: null,
  filterStatus: 'all',
  loading: false,
  error: null,
};

const agentSlice = createSlice({
  name: 'agents',
  initialState,
  reducers: {
    setAgentLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setAgentError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setAgents: (state, action: PayloadAction<AgentItem[]>) => {
      state.agents = (action.payload || []).map(normalizeAgent);
    },
    setSelectedAgent: (state, action: PayloadAction<AgentItem | null>) => {
      state.selectedAgent = action.payload ? normalizeAgent(action.payload) : null;
    },
    setFilterStatus: (state, action: PayloadAction<'all' | 'active' | 'inactive'>) => {
      state.filterStatus = action.payload;
    },
    addAgentReducer: (state, action: PayloadAction<AgentItem>) => {
      const normalized = normalizeAgent(action.payload);
      const existingIdx = state.agents.findIndex((a) => a.id === normalized.id);
      if (existingIdx !== -1) {
        state.agents[existingIdx] = normalized;
      } else {
        state.agents.unshift(normalized);
      }
    },
    toggleAgentStatusReducer: (
      state,
      action: PayloadAction<{
        id: string;
        is_active: boolean;
        activated_at?: string | null;
        deactivated_at?: string | null;
      }>
    ) => {
      const { id, is_active, activated_at, deactivated_at } = action.payload;
      const agent = state.agents.find((a) => a.id === id);
      if (agent) {
        agent.is_active = is_active;
        agent.isActive = is_active;
        if (activated_at !== undefined) agent.activated_at = activated_at;
        if (deactivated_at !== undefined) agent.deactivated_at = deactivated_at;
      }
      if (state.selectedAgent && state.selectedAgent.id === id) {
        state.selectedAgent.is_active = is_active;
        state.selectedAgent.isActive = is_active;
        if (activated_at !== undefined) state.selectedAgent.activated_at = activated_at;
        if (deactivated_at !== undefined) state.selectedAgent.deactivated_at = deactivated_at;
      }
    },
  },
});

export const {
  setAgentLoading,
  setAgentError,
  setAgents,
  setSelectedAgent,
  setFilterStatus,
  addAgentReducer,
  toggleAgentStatusReducer,
} = agentSlice.actions;

export default agentSlice.reducer;
